/**
 * Helper functions for Cloud Propagation components
 * 
 * This file contains:
 * - Form data interfaces
 * - Data transformation functions
 * - Form validation helpers
 * - Success alert generators
 * 
 * For routing table validation, see cloudPropagationValidation.ts
 * For data formatting/shaping, see formattersShapedData.tsx
 */

export interface BgpNeighbor {
  id: number;
  address: string;
  label: string;
}

export interface CloudPropagationNeighbor {
  bgpNeighborId?: number;
  bgpNeighborIp?: string;
  mode?: string;
  reflectorInstanceId?: number;
}

export interface RuleIds {
  /** UUID from API (subUuid); used in payloads and paths for update/associate */
  propagationRuleId?: number | string;
  inboundId?: number;
  outboundId?: number;
}

// Form data interfaces
export interface InboundFormData extends NexthopRetryFormValues {
  vrfs?: VrfFormData[];
  routingTables?: string[];
  enis?: string[];
  routeTargets?: string[];
  routingTableTagName?: string;
  routingTableTagValue?: string;
  nextHopTagName?: string;
  nextHopTagValue?: string;
  selectedVpc?: string;
  policySelection?: string;
  nextHopSelection?: string;
  persistence?: boolean;
}

export interface VrfFormData extends NexthopRetryFormValues {
  name?: string;
  routingTables?: string[];
  enis?: string[];
  routeTargets?: string[];
  routingTableTagName?: string;
  routingTableTagValue?: string;
  nextHopTagName?: string;
  nextHopTagValue?: string;
  policySelection?: string;
  nextHopSelection?: string;
  selectedVpc?: string;
  persistence?: boolean;
}

export type SubnetConfigMode = "manual" | "subnet_tags" | "routing_table";

export interface TagPair {
  tagName: string;
  tagValue: string;
}

interface ApiTagRow {
  tagName?: string;
  tagValue?: string;
  hopTagName?: string;
  hopTagValue?: string;
}

interface ApiOutboundSubnetRow {
  subnets?: string;
}

interface ApiOutboundRow {
  id?: number;
  outboundId?: number;
  name?: string;
  vpcId?: string;
  subnetConfigType?: string | null;
  outboundType?: string;
  subnetSelectionType?: string;
  filterSubnetsWithinRoutingTables?: boolean;
  filterSubnetsInRoutingTables?: boolean;
  /** KD-612 request/response field names */
  subnetTags?: ApiTagRow[];
  routingTableTags?: ApiTagRow[];
  routingTableSubnetTags?: ApiTagRow[];
  /** Legacy / alternate response keys */
  outboundSubnetTags?: ApiTagRow[];
  dynamicSubnetTags?: ApiTagRow[];
  outboundRoutingTableTags?: ApiTagRow[];
  outboundRoutingTableSubnetTags?: ApiTagRow[];
  dynamicRoutingTableTags?: ApiTagRow[];
  dynamicSubnetFilterTags?: ApiTagRow[];
  OutboundSubnetTags?: ApiTagRow[];
  OutboundRoutingTableTags?: ApiTagRow[];
  OutboundRoutingTableSubnetTags?: ApiTagRow[];
  outboundSubnets?: ApiOutboundSubnetRow[];
  OutboundSubnets?: ApiOutboundSubnetRow[];
  bgpCommunity?: string;
  nexthopIPAddress?: string;
}

type OutboundFormInput = Partial<OutboundFormData> & {
  subnets?: string[];
  bgpCommunity?: string;
  nexthopIPAddress?: string;
  selectedVpc?: string;
};

type InboundDynamicValidationInput = Partial<InboundFormData> & {
  vrfs?: VrfFormData[];
};

export interface OutboundRuleFormData {
  outboundId?: number | null;
  name?: string;
  selectedVpc: string;
  subnetConfigMode: SubnetConfigMode;
  subnets: string[];
  subnetTags: TagPair[];
  routingTableTags: TagPair[];
  filterSubnetsInRoutingTables: boolean;
  subnetFilterTags: TagPair[];
  bgpCommunity: string;
  nexthopIPAddress: string;
  expanded?: boolean;
}

export interface OutboundFormData {
  rules: OutboundRuleFormData[];
  /** @deprecated Legacy flat shape — migrated to rules[] on load */
  subnets?: string[];
  bgpCommunity?: string;
  nexthopIPAddress?: string;
  selectedVpc?: string;
}

function defaultTagPair(): TagPair {
  return { tagName: "", tagValue: "" };
}

export function defaultOutboundRuleForm(): OutboundRuleFormData {
  return {
    outboundId: null,
    name: "",
    selectedVpc: "",
    subnetConfigMode: "manual",
    subnets: [],
    subnetTags: [defaultTagPair()],
    routingTableTags: [defaultTagPair()],
    filterSubnetsInRoutingTables: false,
    subnetFilterTags: [defaultTagPair()],
    bgpCommunity: "",
    nexthopIPAddress: "",
    expanded: true,
  };
}

export function normalizeOutboundFormData(outbound: OutboundFormInput | null | undefined): OutboundFormData {
  if (outbound?.rules && Array.isArray(outbound.rules) && outbound.rules.length > 0) {
    return { rules: outbound.rules.map((r) => normalizeOutboundRule(r)) };
  }
  if (
    outbound?.subnets?.length ||
    outbound?.bgpCommunity ||
    outbound?.nexthopIPAddress ||
    outbound?.selectedVpc
  ) {
    const legacy = defaultOutboundRuleForm();
    legacy.subnets = outbound.subnets || [];
    legacy.bgpCommunity = outbound.bgpCommunity || "";
    legacy.nexthopIPAddress = outbound.nexthopIPAddress || "";
    legacy.selectedVpc = outbound.selectedVpc || "";
    return { rules: [legacy] };
  }
  return { rules: [defaultOutboundRuleForm()] };
}

function normalizeOutboundRule(rule: Partial<OutboundRuleFormData>): OutboundRuleFormData {
  const base = defaultOutboundRuleForm();
  return {
    ...base,
    ...rule,
    outboundId: rule.outboundId ?? null,
    subnetTags:
      Array.isArray(rule.subnetTags) && rule.subnetTags.length > 0
        ? rule.subnetTags
        : base.subnetTags,
    routingTableTags:
      Array.isArray(rule.routingTableTags) && rule.routingTableTags.length > 0
        ? rule.routingTableTags
        : base.routingTableTags,
    subnetFilterTags:
      Array.isArray(rule.subnetFilterTags) && rule.subnetFilterTags.length > 0
        ? rule.subnetFilterTags
        : base.subnetFilterTags,
    subnets: Array.isArray(rule.subnets) ? rule.subnets : [],
  };
}

function mapApiTagsToForm(tags: ApiTagRow[] | undefined): TagPair[] {
  if (!tags || tags.length === 0) return [defaultTagPair()];
  return tags.map((t) => ({
    tagName: t.tagName ?? t.hopTagName ?? "",
    tagValue: t.tagValue ?? t.hopTagValue ?? "",
  }));
}

function pickApiTagRows(...sources: (ApiTagRow[] | undefined)[]): ApiTagRow[] | undefined {
  for (const source of sources) {
    if (source && source.length > 0) return source;
  }
  return undefined;
}

function mapOutboundSubnetsFromApi(outbound: ApiOutboundRow): string[] {
  const rows = outbound.outboundSubnets ?? outbound.OutboundSubnets;
  if (!rows || rows.length === 0) return [];
  return rows.map((s) => s.subnets ?? "").filter(Boolean);
}

function mapOutboundApiToRule(outbound: ApiOutboundRow): OutboundRuleFormData {
  const rawConfigType =
    outbound.subnetConfigType ??
    outbound.outboundType ??
    outbound.subnetSelectionType ??
    null;
  const configType = rawConfigType == null ? null : String(rawConfigType);

  let subnetConfigMode: SubnetConfigMode = "manual";
  if (configType === "subnet_tags") subnetConfigMode = "subnet_tags";
  else if (configType === "routing_table") subnetConfigMode = "routing_table";
  else if (configType === "null" || configType === "") {
    // Legacy rows: infer mode from populated child collections when subnetConfigType is null
    if (
      pickApiTagRows(
        outbound.routingTableTags,
        outbound.outboundRoutingTableTags,
        outbound.OutboundRoutingTableTags
      )
    ) {
      subnetConfigMode = "routing_table";
    } else if (
      pickApiTagRows(
        outbound.subnetTags,
        outbound.outboundSubnetTags,
        outbound.dynamicSubnetTags,
        outbound.OutboundSubnetTags
      )
    ) {
      subnetConfigMode = "subnet_tags";
    }
  }

  const filterWithinRt =
    outbound.filterSubnetsWithinRoutingTables ??
    outbound.filterSubnetsInRoutingTables;

  let subnetTags = [defaultTagPair()];
  let routingTableTags = [defaultTagPair()];
  let subnetFilterTags = [defaultTagPair()];

  if (subnetConfigMode === "subnet_tags") {
    subnetTags = mapApiTagsToForm(
      pickApiTagRows(
        outbound.subnetTags,
        outbound.outboundSubnetTags,
        outbound.dynamicSubnetTags,
        outbound.OutboundSubnetTags
      )
    );
  } else if (subnetConfigMode === "routing_table") {
    routingTableTags = mapApiTagsToForm(
      pickApiTagRows(
        outbound.routingTableTags,
        outbound.outboundRoutingTableTags,
        outbound.dynamicRoutingTableTags,
        outbound.OutboundRoutingTableTags
      )
    );
    if (filterWithinRt) {
      subnetFilterTags = mapApiTagsToForm(
        pickApiTagRows(
          outbound.routingTableSubnetTags,
          outbound.outboundRoutingTableSubnetTags,
          outbound.OutboundRoutingTableSubnetTags,
          outbound.dynamicSubnetFilterTags,
          // Legacy: filter tags were stored under outboundSubnetTags
          outbound.outboundSubnetTags
        )
      );
    }
  }

  return {
    outboundId: outbound.outboundId ?? outbound.id ?? null,
    name: outbound.name || "",
    selectedVpc: outbound.vpcId || "",
    subnetConfigMode,
    subnets: mapOutboundSubnetsFromApi(outbound),
    subnetTags,
    routingTableTags,
    filterSubnetsInRoutingTables: !!filterWithinRt,
    subnetFilterTags,
    bgpCommunity: outbound.bgpCommunity || "",
    nexthopIPAddress: outbound.nexthopIPAddress || "",
    expanded: false,
  };
}

export function mapOutboundRulesFromApi(outbounds: ApiOutboundRow[] | undefined): OutboundRuleFormData[] {
  if (!outbounds || outbounds.length === 0) return [defaultOutboundRuleForm()];
  return outbounds.map(mapOutboundApiToRule);
}

function isCompleteTagPair(pair: TagPair): boolean {
  return !!(pair.tagName?.trim() && pair.tagValue?.trim());
}

export function hasMeaningfulOutboundRule(rule: OutboundRuleFormData): boolean {
  const hasVpc = !!(rule.selectedVpc && String(rule.selectedVpc).trim());
  const hasSubnets = Array.isArray(rule.subnets) && rule.subnets.length > 0;
  const hasCommunity = !!(rule.bgpCommunity && String(rule.bgpCommunity).trim());
  const hasNextHop = !!(rule.nexthopIPAddress && String(rule.nexthopIPAddress).trim());
  const hasSubnetTags = (rule.subnetTags || []).some(isCompleteTagPair);
  const hasRoutingTags = (rule.routingTableTags || []).some(isCompleteTagPair);
  const hasFilterTags = (rule.subnetFilterTags || []).some(isCompleteTagPair);
  return (
    hasVpc ||
    hasSubnets ||
    hasCommunity ||
    hasNextHop ||
    hasSubnetTags ||
    hasRoutingTags ||
    hasFilterTags
  );
}

export function validateOutboundRules(rules: OutboundRuleFormData[]): string | null {
  for (let i = 0; i < rules.length; i++) {
    const rule = rules[i];
    const label = `Rule ${i + 1}`;
    // This function only runs once at least one rule in the set has meaningful
    // data (see hasMeaningfulOutboundFormData gating in CPModal), so a rule
    // with nothing filled in here is never "the untouched default row" — it's
    // an extra card the user added and left blank. Silently skipping it used
    // to drop it from the save payload while Save still reported success
    // (CTX-155); flag it instead so the user fills it in or removes it.
    if (!hasMeaningfulOutboundRule(rule)) {
      return `${label}: fill in the required fields (VPC and subnet configuration) or remove this rule.`;
    }
    if (!rule.selectedVpc?.trim()) return `${label}: VPC is required.`;
    if (rule.subnetConfigMode === "manual" && (!rule.subnets || rule.subnets.length === 0)) {
      return `${label}: Select at least one subnet for manual selection.`;
    }
    if (rule.subnetConfigMode === "subnet_tags") {
      const valid = (rule.subnetTags || []).filter(isCompleteTagPair);
      if (valid.length === 0) return `${label}: Add at least one complete subnet TAG:Value pair.`;
    }
    if (rule.subnetConfigMode === "routing_table") {
      const rtValid = (rule.routingTableTags || []).filter(isCompleteTagPair);
      if (rtValid.length === 0) return `${label}: Add at least one routing table TAG:Value pair.`;
      if (rule.filterSubnetsInRoutingTables) {
        const sfValid = (rule.subnetFilterTags || []).filter(isCompleteTagPair);
        if (sfValid.length === 0) {
          return `${label}: Add at least one subnet filter TAG:Value pair.`;
        }
      }
    }
  }
  return null;
}

function hasMeaningfulVrf(vrf: VrfFormData): boolean {
  const hasRoutingTables = Array.isArray(vrf.routingTables) && vrf.routingTables.length > 0;
  const hasEnis = Array.isArray(vrf.enis) && vrf.enis.length > 0;
  const hasRouteTargets = Array.isArray(vrf.routeTargets) && vrf.routeTargets.length > 0;
  const hasDynamicRouting = !!(vrf.routingTableTagName?.trim() || vrf.routingTableTagValue?.trim());
  const hasDynamicNextHop = !!(vrf.nextHopTagName?.trim() || vrf.nextHopTagValue?.trim());
  const hasVpc = !!(vrf.selectedVpc && String(vrf.selectedVpc).trim());
  return hasRoutingTables || hasEnis || hasRouteTargets || hasDynamicRouting || hasDynamicNextHop || hasVpc;
}

export function validateInboundDynamicTags(
  inbound: InboundDynamicValidationInput | null | undefined,
  isTelco: boolean
): string | null {
  if (isTelco) {
    const vrfs = inbound?.vrfs || [];
    for (let i = 0; i < vrfs.length; i++) {
      const vrf = vrfs[i];
      const label = vrf.name || `VRF ${i + 1}`;
      // Same reasoning as validateOutboundRules (CTX-155): this only runs once
      // at least one VRF has meaningful data, so a blank VRF here is an extra
      // "+ Add VRF" card the user forgot to fill in, not the untouched default.
      if (!hasMeaningfulVrf(vrf)) {
        return `${label}: fill in the required fields (VPC and routing configuration) or remove this VRF.`;
      }
      if (vrf.policySelection === "dynamic") {
        if (!vrf.routingTableTagName?.trim() || !vrf.routingTableTagValue?.trim()) {
          return `${label}: Routing table tag name and value are required for dynamic detection.`;
        }
      }
      if (vrf.nextHopSelection === "dynamic") {
        if (!vrf.nextHopTagName?.trim() || !vrf.nextHopTagValue?.trim()) {
          return `${label}: Next-hop tag name and value are required for dynamic detection.`;
        }
      }
    }
    return null;
  }
  if (inbound?.policySelection === "dynamic") {
    if (!inbound.routingTableTagName?.trim() || !inbound.routingTableTagValue?.trim()) {
      return "Routing table tag name and value are required for dynamic detection.";
    }
  }
  if (inbound?.nextHopSelection === "dynamic") {
    if (!inbound.nextHopTagName?.trim() || !inbound.nextHopTagValue?.trim()) {
      return "Next-hop tag name and value are required for dynamic detection.";
    }
  }
  return null;
}

/**
 * Extracts BGP neighbor IDs from selected neighbors or prop
 * @param selectedBgpNeighbors - Array of selected BGP neighbors from dropdown (add mode)
 * @param bgpNeighborId - BGP neighbor ID from prop (edit mode)
 * @returns Array of valid BGP neighbor IDs
 */
export function getBgpNeighborIds(
  selectedBgpNeighbors: BgpNeighbor[],
  bgpNeighborId?: number
): number[] {
  // In add mode: use selectedBgpNeighbors from dropdown
  if (selectedBgpNeighbors.length > 0) {
    return selectedBgpNeighbors
      .map(n => n.id)
      .filter(id => id > 0 && !isNaN(id));
  }
  // In edit mode: use bgpNeighborId prop
  if (bgpNeighborId) {
    return [bgpNeighborId];
  }
  return [];
}

/**
 * Generates a unique key for the current rule being edited
 * @param bgpNeighborId - BGP neighbor ID
 * @param neighborAddress - Neighbor address
 * @param clusterId - Cluster ID
 * @returns Unique key string
 */
export function getCurrentRuleKey(
  bgpNeighborId?: number,
  neighborAddress?: string,
  clusterId?: number
): string {
  return `${bgpNeighborId || 'none'}_${neighborAddress || 'none'}_${clusterId || 'none'}`;
}

/**
 * Checks if inbound form data has meaningful content
 * @param inbound - Inbound form data
 * @param isTelco - Whether in Telco mode
 * @returns True if form has meaningful data
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function hasMeaningfulInboundFormData(inbound: any, isTelco: boolean): boolean {
  if (isTelco) {
    const vrfs = inbound?.vrfs || [];
    if (!Array.isArray(vrfs) || vrfs.length === 0) return false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return vrfs.some((vrf: any) => {
      const hasRoutingTables = Array.isArray(vrf.routingTables) && vrf.routingTables.length > 0;
      const hasEnis = Array.isArray(vrf.enis) && vrf.enis.length > 0;
      const hasRouteTargets = Array.isArray(vrf.routeTargets) && vrf.routeTargets.length > 0;
      const hasDynamicRouting = !!(vrf.routingTableTagName || vrf.routingTableTagValue);
      const hasDynamicNextHop = !!(vrf.nextHopTagName || vrf.nextHopTagValue);
      return hasRoutingTables || hasEnis || hasRouteTargets || hasDynamicRouting || hasDynamicNextHop;
    });
  }

  // Enterprise mode
  const hasRoutingTables = Array.isArray(inbound?.routingTables) && inbound.routingTables.length > 0;
  const hasEnis = Array.isArray(inbound?.enis) && inbound.enis.length > 0;
  const hasDynamicRouting = !!(inbound?.routingTableTagName || inbound?.routingTableTagValue);
  const hasDynamicNextHop = !!(inbound?.nextHopTagName || inbound?.nextHopTagValue);
  const hasRouteTargets = Array.isArray(inbound?.routeTargets) && inbound.routeTargets.length > 0;
  const hasVpc = !!(inbound?.selectedVpc && String(inbound.selectedVpc).trim());
  return hasRoutingTables || hasEnis || hasDynamicRouting || hasDynamicNextHop || hasRouteTargets || hasVpc;
}

/**
 * Checks if outbound form data has meaningful content
 * @param outbound - Outbound form data
 * @returns True if form has meaningful data
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function hasMeaningfulOutboundFormData(outbound: any): boolean {
  const normalized = normalizeOutboundFormData(outbound);
  return normalized.rules.some(hasMeaningfulOutboundRule);
}

/**
 * Generates success alert data for propagation rule operations
 * @param operation - 'create' or 'update'
 * @param ruleType - 'inbound' or 'outbound'
 * @param mode - 'standalone' or 'cluster'
 * @returns Alert data object
 */
export function getSuccessAlertData(
  operation: 'create' | 'update',
  ruleType: 'inbound' | 'outbound',
  mode: 'standalone' | 'cluster'
): {
  title: string;
  description: string;
  variant: 'success';
} {
  const operationText = operation === 'create' ? 'created' : 'updated';
  const modeText = mode === 'cluster' ? 'cluster' : '';
  
  return {
    title: `${ruleType.charAt(0).toUpperCase() + ruleType.slice(1)} propagation rule ${operationText} successfully`,
    description: `The ${ruleType} propagation rule has been ${operationText} successfully${modeText ? ` for the ${modeText}` : ''}.`,
    variant: 'success' as const,
  };
}

/**
 * Transforms API response to form structure
 * @param apiData - API response data
 * @param currentSelectedRow - Current selected row (optional)
 * @returns Object containing transformed data and rule IDs
 */
export function transformRuleDetailsResponse(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  apiData: any,
  currentSelectedRow?: CloudPropagationNeighbor
): {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  result: any;
  ids: RuleIds;
} {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result: any = {};
  const ids: RuleIds = {};
  
  // Unified rule object from GET by UUID (single object with inbounds/outbounds).
  // Legacy format is an array of propagationType-split entries.
  const isNewFormat =
    apiData &&
    !Array.isArray(apiData) &&
    (apiData.bgpNeighbors ||
      apiData.inbounds ||
      apiData.outbounds ||
      apiData.subUuid ||
      apiData.propagationRuleId);

  if (isNewFormat) {
    // New API format: single object with bgpNeighbors, inbounds, outbounds
    // Prefer subUuid / propagationRuleId for payload/path; fallback to id
    if (apiData.subUuid) {
      ids.propagationRuleId = apiData.subUuid;
    } else if (apiData.propagationRuleId != null) {
      ids.propagationRuleId = apiData.propagationRuleId;
    } else if (apiData.id != null) {
      ids.propagationRuleId = apiData.id;
    }
    
    // Determine mode from API response
    const isTelco = apiData.mode === 'TELCO' || apiData.mode === 'Telco';
    
    // Extract BGP neighbors
    const bgpNeighbors: Array<{ id: number; neighborAddress: string; peerAs: number }> = [];
    if (apiData.bgpNeighbors && Array.isArray(apiData.bgpNeighbors)) {
      apiData.bgpNeighbors.forEach((neighbor: { id?: number; neighborAddress?: string; peerAs?: number }) => {
        if (neighbor.id && !bgpNeighbors.find(n => n.id === neighbor.id)) {
          bgpNeighbors.push({
            id: neighbor.id,
            neighborAddress: neighbor.neighborAddress ?? '',
            peerAs: neighbor.peerAs ?? 0
          });
        }
      });
    }
    result._bgpNeighbors = bgpNeighbors;
    if (apiData.name != null && String(apiData.name).trim() !== "") {
      result.name = String(apiData.name).trim();
    }

    // Transform inbound data
    if (apiData.inbounds && apiData.inbounds.length > 0) {
      // Store the first inbound ID (for updates, we'll use individual VRF inboundIds)
      ids.inboundId = apiData.inbounds[0].id;
      
      if (isTelco) {
        // Telco mode: Create VRF structure for all inbounds
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const vrfs = apiData.inbounds.map((inbound: any) => {
          const policySelection = inbound.inboundType === 'specific' ? 'specific' : 'dynamic';
          const hopType = inbound.nextHopSettings?.[0]?.hopType;
          const nextHopSelection = hopType === 'DYNAMIC_ENI_RESOLVER' ? 'dynamic_eni_resolver' : (hopType === 'ENI' || hopType === 'TGW') ? 'specific' : 'dynamic';
          
          // Extract route targets (can be comma-separated string)
          const routeTargets = inbound.routeTargets 
            ? inbound.routeTargets.split(',').map((rt: string) => rt.trim()).filter((rt: string) => rt)
            : [];
          
          const vrfData = {
            name: inbound.vrfName || 'Default VRF',
            routeTargets: routeTargets,
            newRouteTarget: '',
            routingTable: '',
            nextHopEni: '',
            policySelection: policySelection,
            nextHopSelection: nextHopSelection,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            routingTables: inbound.specificRoutes?.map((route: any) => route.routeTable) || [],
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            enis: inbound.nextHopSettings?.[0]?.cloudElementInterfaces?.map((item: any) => item.elementValue) || [],
            routingTableTagName: inbound.dynamicRoutes?.length > 0 ? inbound.dynamicRoutes[0]?.tagName : '',
            routingTableTagValue: inbound.dynamicRoutes?.length > 0 ? inbound.dynamicRoutes[0]?.tagValue : '',
            nextHopTagName: inbound.nextHopSettings?.[0]?.dynamicRouteTags?.length > 0 ? inbound.nextHopSettings[0].dynamicRouteTags[0]?.hopTagName : '',
            nextHopTagValue: inbound.nextHopSettings?.[0]?.dynamicRouteTags?.length > 0 ? inbound.nextHopSettings[0].dynamicRouteTags[0]?.hopTagValue : '',
            expanded: true,
            selectedVpc: inbound.vpcId || '',
            inboundId: inbound.id || null,
            persistence: inbound.persistence ?? true,
            // Retry cadence rides inside nextHopSettings, next to hopType (CTX-180)
            ...readNexthopRetryFromApi(inbound.nextHopSettings?.[0]),
            selectedRoutingTable: '', // Initialize empty
            selectedEni: inbound.nextHopSettings?.[0]?.cloudElementInterfaces?.[0]?.elementValue || '', // Prefill first ENI if available
          };
          return vrfData;
        });

        result.inbound = {
          vrfs: vrfs,
          selectedVpc: vrfs.length > 0 ? vrfs[0].selectedVpc : '',
          // Verification is reflector-wide and lives on the FLAT form (CTX-347).
          // Rows converge on save (the payload writes every VRF identically),
          // so the first inbound's row is the stored state.
          ...readNexthopVerificationFromApi(apiData.inbounds[0]?.nextHopSettings?.[0]),
        };
      } else {
        // Enterprise mode: Use direct field structure
        const inbound = apiData.inbounds[0];
        const policySelection = inbound.inboundType === 'specific' ? 'specific' : 'dynamic';
        const hopType = inbound.nextHopSettings?.[0]?.hopType;
        const nextHopSelection = hopType === 'DYNAMIC_ENI_RESOLVER' ? 'dynamic_eni_resolver' : (hopType === 'ENI' || hopType === 'TGW') ? 'specific' : 'dynamic';
        
        result.inbound = {
          policySelection: policySelection,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          routingTables: inbound.specificRoutes?.map((route: any) => route.routeTable) || [],
          routingTableTagName: inbound.dynamicRoutes?.length > 0 ? inbound.dynamicRoutes[0]?.tagName : '',
          routingTableTagValue: inbound.dynamicRoutes?.length > 0 ? inbound.dynamicRoutes[0]?.tagValue : '',
          nextHopSelection: nextHopSelection,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          enis: inbound.nextHopSettings?.[0]?.cloudElementInterfaces?.map((item: any) => item.elementValue) || [],
          nextHopTagName: inbound.nextHopSettings?.[0]?.dynamicRouteTags?.length > 0 ? inbound.nextHopSettings[0].dynamicRouteTags[0]?.hopTagName : '',
          nextHopTagValue: inbound.nextHopSettings?.[0]?.dynamicRouteTags?.length > 0 ? inbound.nextHopSettings[0].dynamicRouteTags[0]?.hopTagValue : '',
          selectedVpc: inbound.vpcId || '',
          persistence: inbound.persistence ?? true,
          // Retry cadence rides inside nextHopSettings, next to hopType (CTX-180)
          ...readNexthopRetryFromApi(inbound.nextHopSettings?.[0]),
          // Same row read through the verification lens; the CPModal strips
          // whichever field set the reflector's version can't honour (CTX-347).
          ...readNexthopVerificationFromApi(inbound.nextHopSettings?.[0]),
          selectedRoutingTable: '', // Initialize empty
          selectedEni: inbound.nextHopSettings?.[0]?.cloudElementInterfaces?.[0]?.elementValue || '', // Prefill first ENI if available
          inboundId: inbound.id ?? null,
        };
      }
    }

    // Transform outbound data — all outbounds → rules[]
    if (apiData.outbounds && apiData.outbounds.length > 0) {
      ids.outboundId = apiData.outbounds[0].outboundId ?? apiData.outbounds[0].id;
      result.outbound = {
        rules: mapOutboundRulesFromApi(apiData.outbounds),
      };
    }
    
    // Check if any configuration exists
    const hasAnyConfig = (apiData.inbounds && apiData.inbounds.length > 0) || 
                        (apiData.outbounds && apiData.outbounds.length > 0);
    result.hasExistingConfig = hasAnyConfig;
    
    return { result, ids };
  }
  
  // Legacy format: array of rules with propagationType
  // Ensure apiData is an array
  const dataArray = Array.isArray(apiData) ? apiData : [apiData];
  
  // Check if any configuration exists
  const hasAnyConfig = dataArray.some(
    (rule) =>
      (rule.inbounds && rule.inbounds.length > 0) ||
      (rule.outbounds && rule.outbounds.length > 0)
  );
  
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dataArray.forEach((rule: any) => {
    // Prefer subUuid for payload/path; fallback to id
    if (!ids.propagationRuleId) {
      if (rule.subUuid) ids.propagationRuleId = rule.subUuid;
      else if (rule.id != null) ids.propagationRuleId = rule.id;
    }

    if (rule.inbounds && rule.inbounds.length > 0) {
      // Store the inbound rule ID for update operations
      ids.inboundId = rule.inbounds[0].id;
      
      // Check if this is Telco mode
      let isTelco = currentSelectedRow?.mode === 'Telco';
      
      // Fallback: if currentSelectedRow mode is not available, check if the data has Telco-specific structure
      if (isTelco === undefined || isTelco === null) {
        isTelco = !!(rule.inbounds[0].vrfName || rule.inbounds[0].routeTargets);
      }
      
      if (isTelco) {
        // Telco mode: Create VRF structure for all inbounds
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const vrfs = rule.inbounds.map((inbound: any) => {
          const policySelection = inbound.inboundType === 'specific' ? 'specific' : 'dynamic';
          const hopType = inbound.nextHopSettings?.[0]?.hopType;
          const nextHopSelection = hopType === 'DYNAMIC_ENI_RESOLVER' ? 'dynamic_eni_resolver' : (hopType === 'ENI' || hopType === 'TGW') ? 'specific' : 'dynamic';
          
          return {
            name: inbound.vrfName || 'Default VRF',
            routeTargets: inbound.routeTargets ? inbound.routeTargets.split(',').map((rt: string) => rt.trim()).filter((rt: string) => rt) : [],
            newRouteTarget: '',
            routingTable: '',
            nextHopEni: '',
            policySelection: policySelection,
            nextHopSelection: nextHopSelection,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          routingTables: inbound.specificRoutes?.map((route: any) => route.routeTable) || [],
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            enis: inbound.nextHopSettings?.[0]?.cloudElementInterfaces?.map((item: any) => item.elementValue) || 
                  (hopType === 'TGW' && inbound.tgw ? [inbound.tgw] : []) ||
                  (hopType === 'TGW' && inbound.nextHopSettings?.[0]?.tgw ? [inbound.nextHopSettings[0].tgw] : []),
            routingTableTagName: inbound.dynamicRoutes?.length > 0 ? inbound.dynamicRoutes[0]?.tagName : '',
            routingTableTagValue: inbound.dynamicRoutes?.length > 0 ? inbound.dynamicRoutes[0]?.tagValue : '',
            nextHopTagName: inbound.nextHopSettings?.[0]?.dynamicRouteTags?.length > 0 ? inbound.nextHopSettings[0].dynamicRouteTags[0]?.hopTagName : '',
            nextHopTagValue: inbound.nextHopSettings?.[0]?.dynamicRouteTags?.length > 0 ? inbound.nextHopSettings[0].dynamicRouteTags[0]?.hopTagValue : '',
            expanded: true,
            selectedVpc: inbound.vpcId || '',
            inboundId: inbound.id || null,
            persistence: inbound.persistence ?? true,
          };
        });

        result.inbound = {
          vrfs: vrfs,
          selectedVpc: vrfs.length > 0 ? vrfs[0].selectedVpc : '',
        };
      } else {
        // Enterprise mode: Use direct field structure
        const inbound = rule.inbounds[0];
        const policySelection = inbound.inboundType === 'specific' ? 'specific' : 'dynamic';
        const hopType = inbound.nextHopSettings?.[0]?.hopType;
        const nextHopSelection = hopType === 'DYNAMIC_ENI_RESOLVER' ? 'dynamic_eni_resolver' : (hopType === 'ENI' || hopType === 'TGW') ? 'specific' : 'dynamic';
        
        result.inbound = {
          policySelection: policySelection,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          routingTables: inbound.specificRoutes?.map((route: any) => route.routeTable) || [],
          routingTableTagName: inbound.dynamicRoutes?.length > 0 ? inbound.dynamicRoutes[0]?.tagName : '',
          routingTableTagValue: inbound.dynamicRoutes?.length > 0 ? inbound.dynamicRoutes[0]?.tagValue : '',
          nextHopSelection: nextHopSelection,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          enis: inbound.nextHopSettings?.[0]?.cloudElementInterfaces?.map((item: any) => item.elementValue) || 
                (hopType === 'TGW' && inbound.tgw ? [inbound.tgw] : []) ||
                (hopType === 'TGW' && inbound.nextHopSettings?.[0]?.tgw ? [inbound.nextHopSettings[0].tgw] : []),
          nextHopTagName: inbound.nextHopSettings?.[0]?.dynamicRouteTags?.length > 0 ? inbound.nextHopSettings[0].dynamicRouteTags[0]?.hopTagName : '',
          nextHopTagValue: inbound.nextHopSettings?.[0]?.dynamicRouteTags?.length > 0 ? inbound.nextHopSettings[0].dynamicRouteTags[0]?.hopTagValue : '',
          selectedVpc: inbound.vpcId || '',
          persistence: inbound.persistence ?? true,
          bgpNeighborId: rule.bgpNeighborId || currentSelectedRow?.bgpNeighborId || null,
          inboundId: inbound.id ?? null,
        };
      }
    }

    if (rule.outbounds && rule.outbounds.length > 0) {
      ids.outboundId = rule.outbounds[0].outboundId ?? rule.outbounds[0].id;
      result.outbound = {
        rules: mapOutboundRulesFromApi(rule.outbounds),
      };
    }
  });
  
  // Extract propagationRuleId and name from legacy array responses
  if (dataArray.length > 0) {
    const firstRule = dataArray[0] as { id?: number; subUuid?: string; propagationRuleId?: string; name?: string };
    if (firstRule.subUuid) ids.propagationRuleId = firstRule.subUuid;
    else if (firstRule.propagationRuleId != null) ids.propagationRuleId = firstRule.propagationRuleId;
    else if (firstRule.id != null) ids.propagationRuleId = firstRule.id;
    if (firstRule.name != null && String(firstRule.name).trim() !== "") {
      result.name = String(firstRule.name).trim();
    }
  }

  // Store the hasAnyConfig flag in the result
  result.hasExistingConfig = hasAnyConfig;

  return { result, ids };
}

/**
 * Minimum reflector version required to support the dynamic-tag-based propagation options
 * (Inbound routing table / next-hop "Dynamic detection with tag", Outbound "Subnet tags" / "Routing table").
 * Reflectors below this version must have those options disabled in the CPR UI (CTX-163).
 */
export const DYNAMIC_TAG_MIN_VERSION = "2.1.0";

/** Compares two dot-separated numeric version strings. Returns <0, 0, >0 like a standard comparator. */
export function compareVersions(a: string, b: string): number {
  const partsA = a.split(".").map(Number);
  const partsB = b.split(".").map(Number);
  const length = Math.max(partsA.length, partsB.length);
  for (let i = 0; i < length; i++) {
    const diff = (partsA[i] || 0) - (partsB[i] || 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

/**
 * True when `version` is below `minVersion`. Unknown/missing versions never block
 * (avoids disabling options for reflectors whose version hasn't loaded yet).
 */
export function isVersionBelow(version: string | null | undefined, minVersion: string = DYNAMIC_TAG_MIN_VERSION): boolean {
  if (!version) return false;
  return compareVersions(version, minVersion) < 0;
}

/**
 * True when any of the given reflector versions is below `minVersion`.
 * Used for cluster mode, where a propagation rule's BGP neighbors can't be reliably
 * mapped back to a specific reflector instance in the frontend (the dropdown API
 * doesn't expose reflectorInstanceId per neighbor) — so the whole cluster is gated
 * if any of its instances is on an unsupported version.
 */
export function isAnyVersionBelow(versions: Array<string | null | undefined>, minVersion: string = DYNAMIC_TAG_MIN_VERSION): boolean {
  return versions.some((version) => isVersionBelow(version, minVersion));
}

/* -------------------------------------------------------------------------
 * Next-hop resolution retry — "Advanced settings → Retry" (CTX-181)
 * ---------------------------------------------------------------------- */

/**
 * Minimum reflector version that honours the next-hop retry settings.
 * Inclusive: 2.0.16 supports them, 2.0.15 and below do not. Deliberately a
 * separate constant from DYNAMIC_TAG_MIN_VERSION (2.1.0, CTX-163) — the two
 * gates cover different features and must be able to move independently.
 */
export const NEXTHOP_RETRY_MIN_VERSION = "2.0.16";

export const NEXTHOP_RETRY_MIN_INTERVAL_SECONDS = 5;
export const NEXTHOP_RETRY_MAX_INTERVAL_SECONDS = 20;
export const NEXTHOP_RETRY_MIN_ATTEMPTS = 1;
export const NEXTHOP_RETRY_MAX_ATTEMPTS = 10;
/** Reflector-side defaults, applied when a rule carries no retry settings. */
export const NEXTHOP_RETRY_DEFAULT_INTERVAL_SECONDS = 5;
export const NEXTHOP_RETRY_DEFAULT_ATTEMPTS = 3;

/**
 * Form fields backing the Retry block. `nexthopRetryEnabled === undefined`
 * means "this form doesn't carry retry settings" — the rule was never configured,
 * the reflector is below NEXTHOP_RETRY_MIN_VERSION (see withoutNexthopRetry), or
 * the operator simply never opened the block. Nothing is sent in that case, so an
 * untouched rule keeps its `null` on the backend and the reflector defaults apply.
 * The first edit materialises all three fields at once (see NexthopRetrySettings).
 * The two numeric fields are undefined only while the user has the input
 * cleared, which validation rejects before save.
 */
export interface NexthopRetryFormValues {
  nexthopRetryEnabled?: boolean;
  nexthopRetryIntervalSeconds?: number;
  nexthopRetryAttempts?: number;
}

/** Wire shape. `0/0` is the backend contract for "retry disabled" (CTX-180). */
export interface NexthopRetryPayload {
  intervalSeconds: number;
  attempts: number;
}

/**
 * Strips the retry fields so a reflector below NEXTHOP_RETRY_MIN_VERSION never
 * receives a setting it can't honour, even if the form was hydrated with values
 * from a rule that was configured while pointed at a newer reflector.
 */
export function withoutNexthopRetry<T extends NexthopRetryFormValues>(values: T): T {
  return {
    ...values,
    nexthopRetryEnabled: undefined,
    nexthopRetryIntervalSeconds: undefined,
    nexthopRetryAttempts: undefined,
  };
}

type InboundNexthopRetryCarrier = NexthopRetryFormValues & { vrfs?: NexthopRetryFormValues[] };

/** withoutNexthopRetry applied to the whole inbound form — every VRF in Telco. */
export function withoutInboundNexthopRetry<T extends InboundNexthopRetryCarrier>(
  inbound: T,
  isTelco: boolean
): T {
  if (!isTelco) return withoutNexthopRetry(inbound);
  return { ...inbound, vrfs: (inbound.vrfs ?? []).map((vrf) => withoutNexthopRetry(vrf)) };
}

/**
 * Serializes the form fields into the wire object, or `undefined` when the form
 * carries no retry settings at all (so the payload builders can omit the key).
 */
export function buildNexthopRetryPayload(
  values: NexthopRetryFormValues | null | undefined
): NexthopRetryPayload | undefined {
  if (!values || values.nexthopRetryEnabled === undefined) return undefined;
  if (values.nexthopRetryEnabled === false) return { intervalSeconds: 0, attempts: 0 };
  return {
    intervalSeconds: values.nexthopRetryIntervalSeconds ?? NEXTHOP_RETRY_DEFAULT_INTERVAL_SECONDS,
    attempts: values.nexthopRetryAttempts ?? NEXTHOP_RETRY_DEFAULT_ATTEMPTS,
  };
}

/**
 * The API returns the retry cadence flat inside each `nextHopSettings` entry —
 * not as the nested object the request carries (CTX-180 contract).
 */
export interface ApiNextHopRetryFields {
  retryIntervalSeconds?: number | null;
  retryAttempts?: number | null;
}

/**
 * Reads stored retry settings back into form fields. `null` on both means the rule
 * was never configured: the form stays empty so an untouched rule is never written
 * back with values it did not have (the UI still *displays* the reflector defaults).
 * An explicit `0/0` means the operator turned retry off, and the inputs fall back
 * to the defaults for when it is switched back on.
 */
export function readNexthopRetryFromApi(
  nextHopSettings: ApiNextHopRetryFields | null | undefined
): NexthopRetryFormValues {
  const intervalSeconds = nextHopSettings?.retryIntervalSeconds;
  const attempts = nextHopSettings?.retryAttempts;
  if (intervalSeconds == null || attempts == null) return {};
  if (intervalSeconds === 0 || attempts === 0) {
    return {
      nexthopRetryEnabled: false,
      nexthopRetryIntervalSeconds: NEXTHOP_RETRY_DEFAULT_INTERVAL_SECONDS,
      nexthopRetryAttempts: NEXTHOP_RETRY_DEFAULT_ATTEMPTS,
    };
  }
  return {
    nexthopRetryEnabled: true,
    nexthopRetryIntervalSeconds: Number(intervalSeconds),
    nexthopRetryAttempts: Number(attempts),
  };
}

/** Validates one Retry block. `label` prefixes the message in Telco (per VRF). */
export function validateNexthopRetryValues(
  values: NexthopRetryFormValues | null | undefined,
  label?: string
): string | null {
  // Disabled or not present: nothing to validate — `0/0` is a valid wire state.
  if (!values || !values.nexthopRetryEnabled) return null;

  const prefix = label ? `${label}: ` : "";
  const interval = values.nexthopRetryIntervalSeconds;
  const attempts = values.nexthopRetryAttempts;

  if (
    interval === undefined ||
    !Number.isInteger(interval) ||
    interval < NEXTHOP_RETRY_MIN_INTERVAL_SECONDS ||
    interval > NEXTHOP_RETRY_MAX_INTERVAL_SECONDS
  ) {
    return `${prefix}Retry interval must be a whole number between ${NEXTHOP_RETRY_MIN_INTERVAL_SECONDS} and ${NEXTHOP_RETRY_MAX_INTERVAL_SECONDS} seconds.`;
  }
  if (
    attempts === undefined ||
    !Number.isInteger(attempts) ||
    attempts < NEXTHOP_RETRY_MIN_ATTEMPTS ||
    attempts > NEXTHOP_RETRY_MAX_ATTEMPTS
  ) {
    return `${prefix}Max retries must be a whole number between ${NEXTHOP_RETRY_MIN_ATTEMPTS} and ${NEXTHOP_RETRY_MAX_ATTEMPTS}.`;
  }
  // No separate product rule: these bounds cap the window at
  // NEXTHOP_RETRY_MAX_INTERVAL_SECONDS × NEXTHOP_RETRY_MAX_ATTEMPTS, well inside the
  // 600 s the backend enforces and the 900 s the reflector clamps to (CTX-179/180).
  return null;
}

/** Walks the inbound form — every VRF in Telco, the flat form in Enterprise. */
export function validateInboundNexthopRetry(
  inbound: InboundDynamicValidationInput | null | undefined,
  isTelco: boolean
): string | null {
  if (!isTelco) return validateNexthopRetryValues(inbound);

  const vrfs = inbound?.vrfs || [];
  for (let i = 0; i < vrfs.length; i++) {
    const error = validateNexthopRetryValues(vrfs[i], vrfs[i].name || `VRF ${i + 1}`);
    if (error) return error;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Next-hop verification (CTX-347). On reflectors ≥ NEXTHOP_WATCH_MIN_VERSION
// the retry engine is replaced by an always-on watcher that re-checks every
// watched next-hop IP against AWS on a fixed cadence. The watcher is a
// SINGLETON per reflector — one timer, one on/off — so the UI exposes it as a
// single reflector-wide block, not per rule/VRF. It rides the SAME wire field
// as the retry (`nexthopRetry.intervalSeconds`), with -1 as the explicit
// "verification off" sentinel; the legacy `0` ("retry off", CTX-180 rows)
// deliberately does NOT disable the watcher — it falls back to the default.
// ---------------------------------------------------------------------------

/** Reflectors at exactly [NEXTHOP_RETRY_MIN_VERSION, this) keep the legacy Retry block. */
export const NEXTHOP_WATCH_MIN_VERSION = "2.0.17";

/** Wire sentinel on `nexthopRetry.intervalSeconds`: verification off. */
export const NEXTHOP_WATCH_DISABLED_SENTINEL = -1;

export const NEXTHOP_WATCH_MIN_INTERVAL_SECONDS = 1;
export const NEXTHOP_WATCH_MAX_INTERVAL_SECONDS = 3600;
/** Reflector-side default cadence, applied when no interval is configured. */
export const NEXTHOP_WATCH_DEFAULT_INTERVAL_SECONDS = 60;

/**
 * Form fields backing the verification block. `nexthopVerificationEnabled ===
 * undefined` means the block was never touched — nothing is sent, the stored
 * value (or the reflector default) stays in force. Lives on the FLAT inbound
 * form even in Telco: the setting is reflector-wide, so there is exactly one
 * block below the VRF list, never one per VRF.
 */
export interface NexthopVerificationFormValues {
  nexthopVerificationEnabled?: boolean;
  nexthopVerificationIntervalSeconds?: number;
}

/**
 * Serializes the verification block into the shared wire object, or
 * `undefined` when untouched. `attempts` has no meaning for the watcher and is
 * sent as 0 purely to keep the wire shape; the WSS aggregation and the
 * reflector read only `intervalSeconds` on this path.
 */
export function buildNexthopVerificationPayload(
  values: NexthopVerificationFormValues | null | undefined
): NexthopRetryPayload | undefined {
  if (!values || values.nexthopVerificationEnabled === undefined) return undefined;
  if (values.nexthopVerificationEnabled === false) {
    return { intervalSeconds: NEXTHOP_WATCH_DISABLED_SENTINEL, attempts: 0 };
  }
  return {
    intervalSeconds:
      values.nexthopVerificationIntervalSeconds ?? NEXTHOP_WATCH_DEFAULT_INTERVAL_SECONDS,
    attempts: 0,
  };
}

/**
 * One dispatcher for the payload builders: rules edited on a ≥ 2.0.17
 * reflector carry verification fields on the flat form and win; otherwise the
 * legacy per-rule retry fields apply. The two field sets never coexist —
 * the modal renders exactly one of the two blocks, by version.
 */
export function buildNexthopSettingsPayload(
  retryValues: NexthopRetryFormValues | null | undefined,
  verificationValues: NexthopVerificationFormValues | null | undefined
): NexthopRetryPayload | undefined {
  return (
    buildNexthopVerificationPayload(verificationValues) ?? buildNexthopRetryPayload(retryValues)
  );
}

/**
 * Reads a stored interval back into verification form fields. `-1` is the
 * explicit off; the legacy `0` reads as ON at the default — that is exactly
 * what the reflector does with it, so the form never claims a safety net is
 * off when it is running.
 */
export function readNexthopVerificationFromApi(
  nextHopSettings: ApiNextHopRetryFields | null | undefined
): NexthopVerificationFormValues {
  const raw = nextHopSettings?.retryIntervalSeconds;
  if (raw == null) return {};
  const intervalSeconds = Number(raw);
  if (intervalSeconds === NEXTHOP_WATCH_DISABLED_SENTINEL) {
    return {
      nexthopVerificationEnabled: false,
      nexthopVerificationIntervalSeconds: NEXTHOP_WATCH_DEFAULT_INTERVAL_SECONDS,
    };
  }
  if (intervalSeconds <= 0) {
    return {
      nexthopVerificationEnabled: true,
      nexthopVerificationIntervalSeconds: NEXTHOP_WATCH_DEFAULT_INTERVAL_SECONDS,
    };
  }
  return {
    nexthopVerificationEnabled: true,
    nexthopVerificationIntervalSeconds: intervalSeconds,
  };
}

/** Validates the single verification block (flat form — never per VRF). */
export function validateNexthopVerificationValues(
  values: NexthopVerificationFormValues | null | undefined
): string | null {
  if (!values || !values.nexthopVerificationEnabled) return null;
  const interval = values.nexthopVerificationIntervalSeconds;
  if (
    interval === undefined ||
    !Number.isInteger(interval) ||
    interval < NEXTHOP_WATCH_MIN_INTERVAL_SECONDS ||
    interval > NEXTHOP_WATCH_MAX_INTERVAL_SECONDS
  ) {
    return `Next-hop verification interval must be a whole number between ${NEXTHOP_WATCH_MIN_INTERVAL_SECONDS} and ${NEXTHOP_WATCH_MAX_INTERVAL_SECONDS} seconds.`;
  }
  return null;
}

/** Strips the verification fields, mirroring withoutNexthopRetry for the new block. */
export function withoutNexthopVerification<T extends NexthopVerificationFormValues>(
  values: T
): T {
  return {
    ...values,
    nexthopVerificationEnabled: undefined,
    nexthopVerificationIntervalSeconds: undefined,
  };
}

/** `nextHopSelection` value for the mode whose ENI is resolved live from BGP. */
export const DYNAMIC_ENI_RESOLVER_SELECTION = "dynamic_eni_resolver";

interface NextHopSelectionCarrier {
  nextHopSelection?: string;
}

/**
 * True when the rule resolves at least one next-hop through the Dynamic ENI
 * Resolver — the only next-hop mode the watcher acts on: a pinned ENI or a tag
 * has nothing to re-resolve. The verification block is hidden otherwise, so the
 * operator is never offered a cadence that cannot change anything. Telco reads
 * every VRF (one shared block for the whole rule), Enterprise the flat form.
 */
export function hasDynamicEniResolverNextHop(
  inbound: (NextHopSelectionCarrier & { vrfs?: NextHopSelectionCarrier[] }) | null | undefined,
  isTelco: boolean
): boolean {
  if (!inbound) return false;
  if (!isTelco) return inbound.nextHopSelection === DYNAMIC_ENI_RESOLVER_SELECTION;
  return (inbound.vrfs ?? []).some(
    (vrf) => vrf.nextHopSelection === DYNAMIC_ENI_RESOLVER_SELECTION
  );
}
